import { useState, useEffect } from "react";
import { CohortService } from "@/services/cohortService";
import {
  Cohort,
  CreateCohortRequest,
  UpdateCohortRequest,
} from "@/types/cohort";

export const useCohorts = (status?: string, trainerEmail?: string) => {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCohorts = async () => {
    try {
      setLoading(true);
      setError(null);

      let cohortsData: Cohort[];

      if (trainerEmail) {
        const trainerId = await CohortService.getTrainerIdByEmail(trainerEmail);
        cohortsData = trainerId
          ? await CohortService.getCohortsByTrainer(trainerId)
          : [];
      } else if (status) {
        cohortsData = await CohortService.getCohortsByStatus(status);
      } else {
        cohortsData = await CohortService.getAllCohorts();
      }

      setCohorts(cohortsData);
    } catch (err) {
      console.error("Error fetching cohorts:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch cohorts");
    } finally {
      setLoading(false);
    }
  };

  const createCohort = async (
    cohortData: CreateCohortRequest,
    currentUserId: string
  ): Promise<string> => {
    try {
      setError(null);
      const cohortId = await CohortService.createCohort(
        cohortData,
        currentUserId
      );

      // Refresh the list after creating
      await fetchCohorts();

      return cohortId;
    } catch (err) {
      console.error("Error creating cohort:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create cohort";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const updateCohort = async (
    id: string,
    updates: UpdateCohortRequest
  ): Promise<void> => {
    try {
      setError(null);
      await CohortService.updateCohort(id, updates);

      // Refresh the list after updating
      await fetchCohorts();
    } catch (err) {
      console.error("Error updating cohort:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update cohort";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const deleteCohort = async (id: string): Promise<void> => {
    try {
      setError(null);
      await CohortService.deleteCohort(id);

      // Refresh the list after deleting
      await fetchCohorts();
    } catch (err) {
      console.error("Error deleting cohort:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete cohort";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const refreshCohorts = () => {
    fetchCohorts();
  };

  useEffect(() => {
    fetchCohorts();
  }, [status, trainerEmail]);

  return {
    cohorts,
    loading,
    error,
    createCohort,
    updateCohort,
    deleteCohort,
    refreshCohorts,
  };
};
